"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { ensureFreshAuthSession, readVerifiedAuthSession } from "@/lib/auth-session";
import styles from "./auth-guard.module.css";

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [allowed, setAllowed] = useState(() => Boolean(readVerifiedAuthSession()));

  useEffect(() => {
    let cancelled = false;
    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    ensureFreshAuthSession()
      .then((session) => {
        if (cancelled) return;
        if (session) {
          setAllowed(true);
          return;
        }
        router.replace(`/login?next=${encodeURIComponent(returnPath)}`);
      })
      .catch(() => {
        if (!cancelled) router.replace(`/login?next=${encodeURIComponent(returnPath)}`);
      });

    return () => {
      cancelled = true;
    };
  }, [pathname, router]);

  if (!allowed) {
    return (
      <main className={`${styles.screen} portal-auth-check${pathname === "/cart" ? " portal-cart-auth-check" : ""}`}>
        <section className={styles.panel} role="status" aria-live="polite" aria-busy="true">
          <span className={styles.brand}>PRVBUY</span>
          <span className={styles.spinner} aria-hidden="true" />
          <div className={styles.copy}>
            <strong>Checking your account…</strong>
            <span>Securely restoring your session.</span>
          </div>
        </section>
      </main>
    );
  }

  return children;
}
